import {createApi} from "@reduxjs/toolkit/query/react"
import baseQuery from "../../utils/apiIIKOConfig"
import {IBasicResponse} from "../../types/api"
import {
    ICourierCreateParams,
    ICourierCreateResponse,
    ICourierDeleteParams,
    ICourierIIKOResponse,
    ICourierListParams,
    ICourierListResponse,
    ICourierUpdateParams
} from "./CourierApiTypes"
import {ICourier} from "./CourierTypes"
import {getPaginationDecision} from "../../utils/pagination"
import {translateUi} from "../../i18n/translateUi"

const COURIER_LIST_PAGE_SIZE = 100

export const courierApi = createApi({
    reducerPath: "courierApi",
    baseQuery,
    tagTypes: ["couriers", "couriers-iiko"],
    endpoints: build => ({
        getCouriers: build.query<IBasicResponse<ICourierListResponse>, ICourierListParams>({
            query: ({page, size, name, phone_number, status, branch_id_list}) => ({
                url: `v1/couriers/list`,
                method: "POST",
                body: {
                    page,
                    size,
                    ...(name ? {name} : {}),
                    ...(phone_number ? {phone_number} : {}),
                    ...(status ? {status} : {}),
                    ...(branch_id_list?.length ? {branch_id_list} : {})
                }
            }),
            providesTags: ["couriers"]
        }),
        getActiveCouriers: build.query<IBasicResponse<ICourierListResponse>, Omit<ICourierListParams, "page" | "size" | "status">>({
            queryFn: async ({name, phone_number, branch_id_list}, _queryApi, _extraOptions, fetchWithBQ) => {
                const couriersById = new Map<string, ICourier>()
                const seenSignatures = new Set<string>()
                let page = 1
                let loadedCount = 0
                let responseMeta: Omit<IBasicResponse<ICourierListResponse>, "data"> = {
                    success: true
                }

                while (true) {
                    const response = await fetchWithBQ({
                        url: "v1/couriers/list",
                        method: "POST",
                        body: {
                            page,
                            size: COURIER_LIST_PAGE_SIZE,
                            status: "ACTIVE",
                            ...(name ? {name} : {}),
                            ...(phone_number ? {phone_number} : {}),
                            ...(branch_id_list?.length ? {branch_id_list} : {})
                        }
                    })

                    if (response.error) {
                        return {error: response.error}
                    }

                    const responseData = response.data as IBasicResponse<ICourierListResponse>
                    if (!responseData?.success || !responseData.data) {
                        return {
                            error: {
                                status: "CUSTOM_ERROR",
                                error: responseData?.message || translateUi("Не удалось загрузить список курьеров")
                            }
                        }
                    }

                    const pageData = responseData.data
                    const content = pageData.content || []
                    const decision = getPaginationDecision({
                        requestedPage: page,
                        pageSize: COURIER_LIST_PAGE_SIZE,
                        loadedCount,
                        pageData,
                        seenSignatures,
                        getItemKey: (courier) => courier.id
                    })

                    if (decision.type === "error") {
                        return {
                            error: {
                                status: "CUSTOM_ERROR",
                                error: translateUi("Не удалось загрузить полный список курьеров")
                            }
                        }
                    }

                    content.filter((courier) => (
                        courier.status?.id === "ACTIVE" && !courier.blocked
                    )).forEach((courier) => couriersById.set(courier.id, courier))

                    responseMeta = {
                        success: responseData.success,
                        message: responseData.message
                    }
                    loadedCount += content.length
                    if (decision.type === "done") {
                        break
                    }

                    seenSignatures.add(decision.signature)
                    page = decision.nextPage
                }

                const couriers = Array.from(couriersById.values())

                return {
                    data: {
                        ...responseMeta,
                        data: {
                            content: couriers,
                            total_pages: Math.max(1, Math.ceil(couriers.length / COURIER_LIST_PAGE_SIZE)),
                            total_elements: couriers.length
                        }
                    }
                }
            },
            providesTags: ["couriers"]
        }),
        getAllCouriers: build.query<IBasicResponse<ICourierListResponse>, Omit<ICourierListParams, "page" | "size" | "status">>({
            queryFn: async ({name, phone_number, branch_id_list}, _queryApi, _extraOptions, fetchWithBQ) => {
                const couriersById = new Map<string, ICourier>()
                const seenSignatures = new Set<string>()
                let page = 1
                let responseMeta: Omit<IBasicResponse<ICourierListResponse>, "data"> = {
                    success: true
                }

                while (true) {
                    const response = await fetchWithBQ({
                        url: "v1/couriers/list",
                        method: "POST",
                        body: {
                            page,
                            size: COURIER_LIST_PAGE_SIZE,
                            ...(name ? {name} : {}),
                            ...(phone_number ? {phone_number} : {}),
                            ...(branch_id_list?.length ? {branch_id_list} : {})
                        }
                    })

                    if (response.error) {
                        return {error: response.error}
                    }

                    const responseData = response.data as IBasicResponse<ICourierListResponse>
                    if (!responseData?.success || !responseData.data) {
                        return {
                            error: {
                                status: "CUSTOM_ERROR",
                                error: responseData?.message || translateUi("Не удалось загрузить список курьеров")
                            }
                        }
                    }

                    const pageData = responseData.data
                    const content = pageData.content || []
                    const decision = getPaginationDecision({
                        requestedPage: page,
                        pageSize: COURIER_LIST_PAGE_SIZE,
                        loadedCount: couriersById.size,
                        pageData,
                        seenSignatures,
                        getItemKey: (courier) => courier.id
                    })

                    if (decision.type === "error") {
                        return {
                            error: {
                                status: "CUSTOM_ERROR",
                                error: translateUi("Не удалось загрузить полный список курьеров")
                            }
                        }
                    }

                    content.forEach((courier) => couriersById.set(courier.id, courier))

                    responseMeta = {
                        success: responseData.success,
                        message: responseData.message
                    }
                    if (decision.type === "done") {
                        break
                    }

                    seenSignatures.add(decision.signature)
                    page = decision.nextPage
                }

                const couriers = Array.from(couriersById.values())

                return {
                    data: {
                        ...responseMeta,
                        data: {
                            content: couriers,
                            total_pages: Math.max(1, Math.ceil(couriers.length / COURIER_LIST_PAGE_SIZE)),
                            total_elements: couriers.length
                        }
                    }
                }
            },
            providesTags: ["couriers"]
        }),
        getCouriersFromIIKO: build.query<IBasicResponse<ICourierIIKOResponse[]>, void>({
            query: () => ({
                url: `v1/couriers/iiko`,
                method: "GET",
            }),
            keepUnusedDataFor: 0,
            providesTags: ["couriers-iiko"]
        }),
        createCourier: build.mutation<IBasicResponse<ICourierCreateResponse>, ICourierCreateParams>({
            query: ({name, password, phone_number, branch_id_list, iiko_id, transport_detail}) => ({
                url: `v1/couriers`,
                method: "POST",
                body: {
                    name,
                    password,
                    phone_number,
                    branch_id_list,
                    iiko_id,
                    transport_detail
                }
            }),
            invalidatesTags: ["couriers", "couriers-iiko"]
        }),
        updateCourier: build.mutation<IBasicResponse<ICourierCreateResponse>, ICourierUpdateParams>({
            query: ({id, name, password, phone_number, branch_id_list, status, blocked, transport_detail}) => ({
                url: `v1/couriers/${id}`,
                method: "PUT",
                body: {
                    name,
                    phone_number,
                    branch_id_list,
                    status,
                    blocked,
                    transport_detail,
                    ...(password ? {password} : {})
                }
            }),
            invalidatesTags: ["couriers", "couriers-iiko"]
        }),
        deleteCourier: build.mutation<IBasicResponse<void>, ICourierDeleteParams>({
            query: ({id}) => ({
                url: `v1/couriers/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["couriers"]
        }),
    })
})

export const {
    useGetCouriersQuery,
    useGetActiveCouriersQuery,
    useGetAllCouriersQuery,
    useCreateCourierMutation,
    useGetCouriersFromIIKOQuery,
    useLazyGetCouriersFromIIKOQuery,
    useUpdateCourierMutation,
    useDeleteCourierMutation
} = courierApi
